import { useEffect, useRef, useState } from "react";
import JSZip from "jszip";
import { Download, FileText, LayoutGrid, List, Loader2, Upload, X } from "lucide-react";
import { SectionToggleBtn, usePersistedOpen } from "../../components/CollapsibleSection";
import { toast } from "sonner";
import proApi, { proErr } from "../../lib/proApi";
import { Checkbox } from "../../components/ui/checkbox";
import { HoverPreview, PreviewToggle, usePreviewPref } from "../../components/HoverPreview";
import { modSelect, noShiftTextSelect } from "../../lib/mouseSelect";
import { pdfFirstPageImage } from "../../lib/pdfThumb";

const fmtD = (iso) => (iso ? new Date(iso).toLocaleDateString("it-IT") : "");
const VIEW_KEY = "pgp_pro_allegati_view";
const CHK = "bg-white border-slate-400 data-[state=checked]:bg-amber-500 data-[state=checked]:border-amber-500";

const Thumb = ({ aid, a }) => {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    const ct = (a.content_type || "").toLowerCase();
    if (!(ct.startsWith("image/") || ct === "application/pdf")) { return; }
    const base = `/professionista/pratiche/${aid}/allegati/${a.id}`;
    let alive = true;
    proApi.get(`${base}/thumb`).then(async ({ data }) => {
      let v = data.url;
      if (!v && ct === "application/pdf") {
        v = await pdfFirstPageImage(`p-${a.id}`, async () => (await proApi.get(base, { responseType: "blob" })).data);
      } else if (!v) {
        v = (await proApi.get(`${base}/content-url`)).data.url;
      }
      if (alive && v) { setUrl(v); }
    }).catch(() => {});
    return () => { alive = false; };
  }, [aid, a.id, a.content_type]);
  if (url) return <img src={url} alt={a.nome} draggable={false} className="h-full w-full object-cover pointer-events-none" loading="lazy" />;
  return <FileText className="h-10 w-10 text-slate-300" />;
};

const uniqueName = (counts, orig) => {
  if (counts[orig] == null) { counts[orig] = 0; return orig; }
  counts[orig] += 1;
  const dot = orig.lastIndexOf(".");
  return dot > 0 ? `${orig.slice(0, dot)} (${counts[orig]})${orig.slice(dot)}` : `${orig} (${counts[orig]})`;
};

const saveBlob = (blob, name) => { const u = URL.createObjectURL(blob); const el = document.createElement("a"); el.href = u; el.download = name; el.click(); URL.revokeObjectURL(u); };

const DlBtn = ({ onClick, testId, className = "" }) => (
  <button type="button" onClick={(e) => { e.stopPropagation(); onClick(); }} title="Scarica" aria-label="Scarica" data-testid={testId}
    className={`h-8 w-8 grid place-items-center rounded-md text-slate-500 hover:text-amber-700 hover:bg-amber-50 shrink-0 ${className}`}><Download className="h-4 w-4" /></button>
);

export const ProAllegati = ({ aid, allegati, onUploaded, readOnly = false }) => {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const [dlBusy, setDlBusy] = useState(false);
  const [view, setView] = useState(() => localStorage.getItem(VIEW_KEY) || "list");
  const [openSec, toggleSec] = usePersistedOpen("pgp_pro_sec_documenti");
  const [prev, setPrev] = usePreviewPref();
  const [sel, setSel] = useState(new Set());
  useEffect(() => { setSel((s) => new Set([...s].filter((id) => allegati.some((a) => a.id === id)))); }, [allegati]);
  const thumbUrl = (a) => {
    const ct = (a.content_type || "").toLowerCase();
    if (!(ct.startsWith("image/") || ct === "application/pdf")) return Promise.resolve(null);
    return proApi.get(`/professionista/pratiche/${aid}/allegati/${a.id}/thumb`).then(({ data }) => data.url || (ct === "application/pdf"
      ? pdfFirstPageImage(`p-${a.id}`, async () => (await proApi.get(`/professionista/pratiche/${aid}/allegati/${a.id}`, { responseType: "blob" })).data)
      : null));
  };
  const setV = (v) => { setView(v); localStorage.setItem(VIEW_KEY, v); };
  const blobOf = (a) => proApi.get(`/professionista/pratiche/${aid}/allegati/${a.id}`, { responseType: "blob" }).then(({ data }) => data);
  const open = async (a) => {
    try { window.open(URL.createObjectURL(await blobOf(a)), "_blank"); } catch (e) { toast.error("File non disponibile"); }
  };
  const scarica = async (a) => {
    try { saveBlob(await blobOf(a), a.nome || "documento"); } catch (e) { toast.error("Download non riuscito"); }
  };
  const scaricaSel = async () => {
    const list = allegati.filter((a) => sel.has(a.id));
    if (!list.length) return;
    setDlBusy(true);
    try {
      if (list.length === 1) { await scarica(list[0]); return; }
      const zip = new JSZip();
      const counts = {};
      for (const a of list) { zip.file(uniqueName(counts, a.nome || "documento"), await blobOf(a)); }
      saveBlob(await zip.generateAsync({ type: "blob" }), "Documenti.zip");
      toast.success(`${list.length} documenti scaricati`);
    } catch (e) { toast.error("Download non riuscito"); } finally { setDlBusy(false); }
  };
  const lastRef = useRef(null);
  const tog = (id) => { lastRef.current = id; setSel((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; }); };
  const modClick = (id) => (e) => modSelect(e, id, allegati.map((a) => a.id), lastRef,
    (x) => setSel((s) => { const n = new Set(s); if (n.has(x)) n.delete(x); else n.add(x); return n; }),
    (ids) => setSel((s) => new Set([...s, ...ids])));
  const allSel = allegati.length > 0 && allegati.every((a) => sel.has(a.id));
  const togAll = () => setSel(allSel ? new Set() : new Set(allegati.map((a) => a.id)));
  const upload = async (files) => {
    setBusy(true);
    try {
      for (const f of files) { const fd = new FormData(); fd.append("file", f); await proApi.post(`/professionista/pratiche/${aid}/upload`, fd); }
      toast.success("File caricati"); onUploaded();
    } catch (e) { toast.error(proErr(e)); } finally { setBusy(false); if (ref.current) ref.current.value = ""; }
  };
  const vbtn = (v, Icon, label) => (
    <button type="button" onClick={() => setV(v)} title={label} data-testid={`pro-view-${v}`}
      className={`h-9 w-9 grid place-items-center rounded-md border ${view === v ? "bg-amber-500 border-amber-500 text-slate-950" : "border-slate-300 text-slate-600 hover:bg-slate-50"}`}><Icon className="h-4 w-4" /></button>
  );
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 sm:p-5 shadow-sm" data-testid="pro-allegati">
      <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
        <button type="button" onClick={toggleSec} data-testid="pro-allegati-title" className="inline-flex items-center gap-1.5 font-semibold text-slate-900">
          Documenti ({allegati.length})
        </button>
        <div className="flex items-center gap-2 flex-wrap">
          {openSec && <>
          <PreviewToggle on={prev} onChange={setPrev} light testId="pro-hover-preview-toggle" />
          {vbtn("list", List, "Vista elenco")}{vbtn("grid", LayoutGrid, "Vista anteprime")}
          </>}
          <input ref={ref} type="file" multiple className="hidden" onChange={(e) => upload(Array.from(e.target.files || []))} data-testid="pro-upload-input" />
          {!readOnly && <button type="button" onClick={() => ref.current?.click()} disabled={busy} data-testid="pro-upload-btn" className="h-9 px-3 rounded-lg bg-amber-500 hover:bg-amber-600 text-slate-950 text-sm font-semibold inline-flex items-center gap-1.5 disabled:opacity-50">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Carica file</button>}
          <SectionToggleBtn open={openSec} onClick={toggleSec} testId="pro-allegati-collapse" />
        </div>
      </div>
      {!openSec && <p className="text-xs text-slate-500">Sezione chiusa: premi «Apri sezione» per vedere i documenti.</p>}
      {openSec && allegati.length === 0 && <p className="text-sm text-slate-500">Nessun documento condiviso.</p>}
      {openSec && allegati.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap mb-3">
          <button type="button" onClick={togAll} data-testid="pro-select-all" className="h-8 px-3 rounded-md border border-slate-300 text-sm text-slate-700 hover:bg-slate-50 inline-flex items-center gap-2">
            <Checkbox checked={allSel} className={`${CHK} pointer-events-none`} tabIndex={-1} /> {allSel ? "Deseleziona tutto" : "Seleziona tutto"}
          </button>
          {sel.size > 0 && (
            <div className="flex items-center gap-2 flex-wrap rounded-lg bg-amber-50 border border-amber-200 px-2 py-1" data-testid="pro-selection-bar">
              <span className="text-sm font-medium text-amber-900" data-testid="pro-selection-count">{sel.size} {sel.size === 1 ? "selezionato" : "selezionati"}</span>
              <button type="button" onClick={scaricaSel} disabled={dlBusy} data-testid="pro-sel-download" className="h-8 px-3 rounded-md bg-amber-500 hover:bg-amber-600 text-slate-950 text-sm font-semibold inline-flex items-center gap-1.5 disabled:opacity-50">
                {dlBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Scarica selezionati
              </button>
              <button type="button" onClick={() => setSel(new Set())} data-testid="pro-sel-clear" className="h-8 px-2 rounded-md text-sm text-slate-600 hover:bg-amber-100 inline-flex items-center gap-1"><X className="h-4 w-4" /> Annulla</button>
            </div>
          )}
        </div>
      )}
      {openSec && allegati.length > 1 && <p className="text-[11px] text-slate-400 mb-2 hidden sm:block" data-testid="pro-select-hint">Suggerimento: Ctrl+clic (Cmd su Mac) per scegliere più file, Maiusc+clic per un intervallo.</p>}
      {!openSec ? null : view === "grid" ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3" data-testid="pro-allegati-grid">
          {allegati.map((a) => (
            <HoverPreview key={a.id} enabled={prev} loadUrl={() => thumbUrl(a)} cacheKey={`pro-${a.id}`} title={a.nome}>
            <div onClickCapture={modClick(a.id)} onMouseDown={noShiftTextSelect} data-testid={`pro-item-${a.id}`} className={`relative rounded-lg border overflow-hidden hover:shadow transition-shadow select-none ${sel.has(a.id) ? "border-amber-500 ring-1 ring-amber-500" : "border-slate-200 hover:border-amber-400"}`}>
              <button type="button" onClick={() => open(a)} data-testid={`pro-allegato-${a.id}`} className="block w-full text-left">
                <div className="aspect-[4/3] bg-slate-50 grid place-items-center overflow-hidden"><Thumb aid={aid} a={a} /></div>
                <div className="p-2 pr-9"><div className="text-xs font-medium text-slate-800 truncate">{a.nome}</div><div className="text-[10px] text-slate-400">{a.mio ? "caricato da te · " : ""}{fmtD(a.created_at)}</div></div>
              </button>
              <div className="absolute top-1.5 left-1.5 rounded bg-white/90 p-1 shadow-sm"><Checkbox checked={sel.has(a.id)} onCheckedChange={() => tog(a.id)} className={CHK} data-testid={`pro-check-${a.id}`} aria-label={`Seleziona ${a.nome}`} /></div>
              <DlBtn onClick={() => scarica(a)} testId={`pro-download-${a.id}`} className="absolute bottom-1 right-1" />
            </div>
            </HoverPreview>
          ))}
        </div>
      ) : (
        <div className="divide-y divide-slate-100" data-testid="pro-allegati-list">
          {allegati.map((a) => (
            <HoverPreview key={a.id} enabled={prev} loadUrl={() => thumbUrl(a)} cacheKey={`pro-${a.id}`} title={a.nome}>
            <div onClickCapture={modClick(a.id)} onMouseDown={noShiftTextSelect} data-testid={`pro-item-${a.id}`} className={`flex items-center gap-2 py-1.5 px-1 rounded select-none ${sel.has(a.id) ? "bg-amber-50" : ""}`}>
              <Checkbox checked={sel.has(a.id)} onCheckedChange={() => tog(a.id)} className={CHK} data-testid={`pro-check-${a.id}`} aria-label={`Seleziona ${a.nome}`} />
              <button type="button" onClick={() => open(a)} data-testid={`pro-allegato-${a.id}`} className="min-w-0 flex-1 text-left flex items-center gap-2 text-sm text-slate-800 hover:text-amber-700">
                <FileText className="h-4 w-4 text-slate-400 shrink-0" /><span className="truncate flex-1">{a.nome}</span>
                <span className="hidden sm:inline text-xs text-slate-400 shrink-0">{a.mio ? "caricato da te · " : ""}{fmtD(a.created_at)}</span>
              </button>
              <DlBtn onClick={() => scarica(a)} testId={`pro-download-${a.id}`} />
            </div>
            </HoverPreview>
          ))}
        </div>
      )}
    </div>
  );
};
